// ADR 17.10.26h §1/§2 — the tips each web screen gives.
//
// Pure data, no React and no wasm: the target components import this file and
// `anchors.ts` only, so a target on a boot-graph page pulls nothing heavy in
// (17.10.26g §3). Each set is a native `CoachMarkScreen` slug with the steps
// the web can honestly point at; a step whose target is not drawn is skipped
// by the machine (9.8.26e), and a set with none presents and latches nothing.
//
// Steps reuse the native `coach_*` keys verbatim except the five `_web` keys
// (§4), whose native text names a gesture or control the web does not draw.

import type { CoachStep } from '../coachMachine'
import type { CoachScreen } from '../screens'

export const COACH_ANCHORS = {
  inputContext: 'lp_input.context', inputMic: 'lp_input.mic', vocabGenerate: 'vocab.generate',
  reviewReveal: 'flashcard.reveal', reviewCard: 'flashcard.card', listPin: 'flashcard.pin',
  listCreate: 'lp_list.create', exercisePrompt: 'lp_exercise.prompt', exerciseMic: 'lp_exercise.mic',
  detailPlay: 'lp_detail.play', detailRemix: 'lp_detail.remix', walkPager: 'walkthrough.pager',
  walkPin: 'walkthrough.pin', walkIndex: 'walkthrough.index',
  // `settings.${slug}`: the same ids C3's tour uses (C3 §5), one per category row (§3)
  settingsLanguage: 'settings.language', settingsLearning: 'settings.learning',
} as const

export interface ScreenTipSet {
  screen: CoachScreen
  steps: readonly CoachStep[] // module-scope constants, as 17.10.26g §4 asks
  yieldTo?: CoachScreen // §6: also hold while a tour is pending on this slug
}

/** One step; the key stem names `<stem>_title` / `<stem>_desc` unless a web key overrides one. */
function tip(anchor: string, stem: string, keys?: { titleKey?: string; bodyKey?: string }): CoachStep {
  return { id: anchor, anchor, titleKey: keys?.titleKey ?? `${stem}_title`, bodyKey: keys?.bodyKey ?? `${stem}_desc` }
}

const A = COACH_ANCHORS

export const TIPS: Record<
  'input' | 'vocab' | 'review' | 'listPin' | 'planList' | 'exercise' | 'detail' | 'walkthrough',
  ScreenTipSet
> = {
  input: {
    screen: 'lesson_plan_input',
    steps: [tip(A.inputContext, 'coach_lp_input_context'), tip(A.inputMic, 'coach_lp_input_mic')],
    yieldTo: 'home',
  },
  vocab: { screen: 'vocab', steps: [tip(A.vocabGenerate, 'coach_vocab_generate')] },
  review: {
    screen: 'flashcard',
    steps: [
      tip(A.reviewReveal, 'coach_flashcard_flip', { bodyKey: 'coach_flashcard_flip_desc_web' }),
      tip(A.reviewCard, 'coach_flashcard_swipe'),
    ],
  },
  listPin: {
    screen: 'flashcard',
    steps: [tip(A.listPin, 'coach_flashcard_pin', { bodyKey: 'coach_flashcard_pin_desc_web' })],
  },
  planList: { screen: 'lesson_plan_list', steps: [tip(A.listCreate, 'coach_lp_list_create')] },
  exercise: {
    screen: 'lesson_plan_exercise',
    steps: [tip(A.exercisePrompt, 'coach_lp_exercise_prompt'), tip(A.exerciseMic, 'coach_lp_exercise_mic')],
  },
  detail: {
    screen: 'lesson_plan_detail',
    steps: [tip(A.detailPlay, 'coach_walkthrough_play'), tip(A.detailRemix, 'coach_lp_detail_remix')],
  },
  walkthrough: {
    screen: 'walkthrough',
    steps: [
      tip(A.walkPager, 'coach_walkthrough_pager', {
        titleKey: 'coach_walkthrough_pager_title_web',
        bodyKey: 'coach_walkthrough_pager_desc_web',
      }),
      tip(A.walkPin, 'coach_walkthrough_pin', { bodyKey: 'coach_walkthrough_pin_desc_web' }),
      tip(A.walkIndex, 'coach_walkthrough_index'),
    ],
  },
}

const LANGUAGE_STEP = tip(A.settingsLanguage, 'coach_settings_language')
const SETTINGS_WITH_REMINDER: ScreenTipSet = {
  screen: 'settings',
  steps: [LANGUAGE_STEP, tip(A.settingsLearning, 'coach_settings_reminder')],
}
const SETTINGS_WITHOUT_REMINDER: ScreenTipSet = { screen: 'settings', steps: [LANGUAGE_STEP] }

/** The reminder step only where the reminder section can render (`ReminderSection`'s own gate). */
export function settingsTips(reminderShown: boolean): ScreenTipSet {
  return reminderShown ? SETTINGS_WITH_REMINDER : SETTINGS_WITHOUT_REMINDER
}
